import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../apiClient";
import { Link } from "react-router-dom";
import { formatMoney } from "../formatMoney";



const Periods = () => {
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [defaultCurrency, setDefaultCurrency] = useState("");
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [sortConfig, setSortConfig] = useState({ key: "DateEnd", direction: "desc" });

    const filteredPeriods = useMemo(() => {
      const query = search.trim().toLowerCase();
      const filtered = periods.filter((period) => {
        const matchesSearch = !query || [period.Description, period.DateStart, period.DateEnd]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
        const matchesActive =
          activeFilter === "all" ||
          (activeFilter === "active" && Number(period.ActiveYesNo) === 1) ||
          (activeFilter === "inactive" && Number(period.ActiveYesNo) === 0);
        return matchesSearch && matchesActive;
      });
      return [...filtered].sort((left, right) => {
        const leftValue = left[sortConfig.key];
        const rightValue = right[sortConfig.key];
        const leftNumeric = Number(leftValue);
        const rightNumeric = Number(rightValue);
        const comparison = Number.isFinite(leftNumeric) && Number.isFinite(rightNumeric)
          ? leftNumeric - rightNumeric
          : String(leftValue || "").localeCompare(String(rightValue || ""));
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }, [periods, search, activeFilter, sortConfig]);

    const handleSort = (key) => {
      setSortConfig((current) => (
        current.key === key
          ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
          : { key, direction: key === "DateEnd" ? "desc" : "asc" }
      ));
    };

    useEffect (() => {
        const fetchAllPeriods = async () => {
            try {
                const [periodsRes, currenciesRes] = await Promise.all([
                  apiClient.get("/periods"),
                  apiClient.get("/currencies"),
                ]);
                setPeriods(Array.isArray(periodsRes.data) ? periodsRes.data : []);
                const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
                setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
            } catch (err) {
                setError("Failed to load periods.");
            } finally {
                setLoading(false);
            }
        };
        fetchAllPeriods();
    }, []);

    const handleDelete = async (PeriodId) => {
        try {
          await apiClient.delete(`/periods/${PeriodId}`);
          setPeriods((prev) => prev.filter((item) => item.PeriodId !== PeriodId));
        } catch (err) {
          setError("Delete failed. Please try again.");
        }
      };

    return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Periods</h1>
          <p className="page-subtitle"><Link to="/">Home</Link> / Periods</p>
        </div>
        <div className="actions">
          <Link className="btn-link" to="/NetWorth">Net Worth</Link>
          <Link className="btn-link" to="/PeriodsAdd">Add Period</Link>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading periods...</p>}

      {!loading && (
        <section className="resource-list">
          {periods.length === 0 ? (
            <article className="panel empty-state">
              <h2>No periods yet</h2>
              <p className="notice notice-muted">Create a reporting period before recording transactions, net worth, or period-based reports.</p>
              <div className="form-actions">
                <Link className="btn" to="/PeriodsAdd">Add First Period</Link>
              </div>
            </article>
          ) : (
            <>
              <section className="panel page-cta list-filter-panel">
                <div className="list-filters">
                  <label className="list-filter-field">
                    <span>Search</span>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Find a period"
                    />
                  </label>
                  <label className="list-filter-field list-filter-field-compact">
                    <span>Status</span>
                    <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </label>
                </div>
              </section>

            <div className="table-scroll">
              <table className="resource-table">
                <thead>
                  <tr>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("Description")}>Period</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("DateStart")}>Start</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("DateEnd")}>End</button></th>
                    <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("IncomeAmount")}>Income</button></th>
                    <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("ExpensesAmount")}>Expenses</button></th>
                    <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("SavingsAmount")}>Savings</button></th>
                    <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("SavingsIncomeAmount")}>Savings Income</button></th>
                    <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("LiabilitiesAmount")}>Liabilities</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("Notes")}>Notes</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("ActiveYesNo")}>Active</button></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPeriods.map((period) => (
                    <tr key={period.PeriodId}>
                      <td data-label="Period"><strong>{period.Description}</strong></td>
                      <td data-label="Start">{period.DateStart}</td>
                      <td data-label="End">{period.DateEnd}</td>
                      <td data-label="Income" className="table-number table-money">{formatMoney(period.IncomeAmount || 0, defaultCurrency)}</td>
                      <td data-label="Expenses" className="table-number table-money">{formatMoney(period.ExpensesAmount || 0, defaultCurrency)}</td>
                      <td data-label="Savings" className="table-number table-money">{formatMoney(period.SavingsAmount || 0, defaultCurrency)}</td>
                      <td data-label="Savings Income" className="table-number table-money">{formatMoney(period.SavingsIncomeAmount || 0, defaultCurrency)}</td>
                      <td data-label="Liabilities" className="table-number table-money">{formatMoney(period.LiabilitiesAmount || 0, defaultCurrency)}</td>
                      <td data-label="Notes">{period.Notes || "-"}</td>
                      <td data-label="Active">{Number(period.ActiveYesNo) ? "Yes" : "No"}</td>
                      <td data-label="Actions" className="resource-table-actions">
                        <Link className="btn-link" to={`/PeriodsUpdate/${period.PeriodId}`}>Edit</Link>
                        <button className="action-link action-link-danger" onClick={() => handleDelete(period.PeriodId)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
      )}
    </div>
    );
};

export default Periods;
